import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Download, 
  Share2, 
  Shirt, 
  ArrowRight, 
  CheckCircle, 
  Ruler,
  User,
  Eye
} from 'lucide-react';

const ResultsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [selectedMeasurement, setSelectedMeasurement] = useState<string | null>(null);

  // Mock data for demo
  const mockMeasurements = {
    height: 180,
    chest: 100,
    waist: 80,
    inseam: 80,
    sleeve: 64,
    shoulder: 45,
    neck: 38,
    bicep: 32,
    forearm: 28,
    thigh: 58,
    calf: 36
  };

  const formatMeasurement = (value: number) => {
    return `${value} cm`;
  };

  const exportMeasurements = () => {
    const data = {
      measurements: mockMeasurements,
      user_id: userId,
      created_at: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `measurements_${userId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const shareResults = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Virtual Tailor Measurements',
          text: 'Check out my AI-powered body measurements from Virtual Tailor!',
          url: window.location.href,
        });
      } catch (_error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">My Results</h1>
          <p className="text-xl text-gray-600">Your AI-powered body measurements and 3D model</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* 3D Avatar Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="card-elevated p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">3D Body Model</h2>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Eye className="w-4 h-4" />
                <span>Interactive View</span>
              </div>
            </div>
            
            <div className="bg-gray-100 rounded-xl h-96 flex items-center justify-center mb-6">
              <div className="text-center">
                <User className="w-24 h-24 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">3D Avatar Viewer</p>
                <p className="text-sm text-gray-500">Your personalized body model</p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button className="btn-outline flex-1">
                <Shirt className="w-4 h-4 mr-2" />
                Try On Clothes
              </button>
              <button className="btn-outline flex-1">
                <Download className="w-4 h-4 mr-2" />
                Export Model
              </button>
            </div>
          </motion.div>

          {/* Measurements Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            {/* Key Measurements */}
            <div className="card-elevated p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">Key Measurements</h2>
                <Ruler className="w-5 h-5 text-blue-600" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-blue-600 font-medium">Height</div>
                  <div className="text-2xl font-bold text-gray-900">{formatMeasurement(mockMeasurements.height)}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-sm text-green-600 font-medium">Chest</div>
                  <div className="text-2xl font-bold text-gray-900">{formatMeasurement(mockMeasurements.chest)}</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-sm text-purple-600 font-medium">Waist</div>
                  <div className="text-2xl font-bold text-gray-900">{formatMeasurement(mockMeasurements.waist)}</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-sm text-orange-600 font-medium">Inseam</div>
                  <div className="text-2xl font-bold text-gray-900">{formatMeasurement(mockMeasurements.inseam)}</div>
                </div>
              </div>
            </div>

            {/* Detailed Measurements */}
            <div className="card-elevated p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Measurements</h3>
              <div className="space-y-3">
                {Object.entries(mockMeasurements).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                    <span className="text-gray-700 capitalize">{key}</span>
                    <span className="font-semibold text-gray-900">{formatMeasurement(value)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="card-elevated p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/try-on')}
                  className="w-full btn-primary"
                >
                  <Shirt className="w-4 h-4 mr-2" />
                  Try On Clothes
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={exportMeasurements}
                    className="btn-outline"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </button>
                  <button
                    onClick={shareResults}
                    className="btn-outline"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Results
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Confidence Indicator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 card-elevated p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <div>
                <h3 className="font-semibold text-gray-900">High Confidence Results</h3>
                <p className="text-sm text-gray-600">All measurements have high accuracy scores</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-600">98.5%</div>
              <div className="text-sm text-gray-500">Accuracy</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultsPage; 